'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, ArrowUpRight, Building2, Smartphone, Loader2, Check, Shield, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface WithdrawModalProps {
  onClose: () => void;
  currency: string;
  symbol: string;
}

interface WithdrawFormData {
  amount: string;
  method: string;
  accountDetails: string;
}

const withdrawMethods = [
  { id: 'wm-bank', value: 'bank', label: 'Bank Transfer', icon: Building2, sublabel: '1–3 business days', fee: '0%' },
  { id: 'wm-upi', value: 'upi', label: 'UPI', icon: Smartphone, sublabel: 'Instant (India only)', fee: '0%' },
];

export default function WithdrawModal({ onClose, currency, symbol }: WithdrawModalProps) {
  const [step, setStep] = useState<'form' | 'review' | 'loading' | 'success'>('form');
  const [selectedMethod, setSelectedMethod] = useState('bank');

  const { register, handleSubmit, formState: { errors }, watch } = useForm<WithdrawFormData>();
  const amountValue = watch('amount');
  const numAmount = parseFloat(amountValue) || 0;
  const availableBalance = 109.50;

  const onSubmitForm = () => setStep('review');

  const onConfirm = async () => {
    setStep('loading');
    // Backend integration point: POST /api/wallet/withdraw — triggers compliance review queue
    await new Promise((r) => setTimeout(r, 2000));
    setStep('success');
    toast.success('Withdrawal request submitted. Under compliance review.');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-base font-bold text-foreground">
            {step === 'review' ? 'Confirm Withdrawal' : step === 'success' ? 'Withdrawal Submitted' : 'Withdraw Funds'}
          </h3>
          {step !== 'loading' && (
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors" aria-label="Close">
              <X size={16} style={{ color: 'var(--muted-foreground)' }} />
            </button>
          )}
        </div>

        <div className="p-5">
          {step === 'form' && (
            <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-5">
              {/* Available balance */}
              <div className="flex items-center justify-between p-3 rounded-xl"
                style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <span className="text-sm font-semibold" style={{ color: 'var(--muted-foreground)' }}>Withdrawable Balance</span>
                <span className="text-lg font-extrabold" style={{ color: '#10B981', fontVariantNumeric: 'tabular-nums' }}>
                  {symbol}{availableBalance.toFixed(2)}
                </span>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-foreground" htmlFor="withdraw-amount">
                  Withdrawal Amount ({currency})
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Minimum: {symbol}10 · Maximum: {symbol}{availableBalance.toFixed(2)} (your balance)
                </p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: 'var(--muted-foreground)' }}>
                    {symbol}
                  </span>
                  <input
                    id="withdraw-amount"
                    type="number"
                    step="0.01"
                    min="10"
                    max={availableBalance}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-3 text-lg font-bold rounded-xl outline-none transition-all"
                    style={{
                      background: 'var(--input)',
                      border: `1px solid ${errors.amount ? '#F43F5E' : 'var(--border)'}`,
                      color: 'var(--foreground)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    onFocus={(e) => { if (!errors.amount) e.target.style.borderColor = 'var(--primary)'; }}
                    onBlur={(e) => { if (!errors.amount) e.target.style.borderColor = 'var(--border)'; }}
                    {...register('amount', {
                      required: 'Please enter an amount',
                      min: { value: 10, message: `Minimum withdrawal is ${symbol}10` },
                      max: { value: availableBalance, message: `Cannot exceed withdrawable balance of ${symbol}${availableBalance}` },
                    })}
                  />
                </div>
                {errors.amount && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.amount.message}</p>}
              </div>

              {/* Method */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-foreground">Withdrawal Method</label>
                <div className="space-y-2">
                  {withdrawMethods.map((method) => (
                    <label
                      key={method.id}
                      className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all"
                      style={{
                        background: selectedMethod === method.value ? 'rgba(124,58,237,0.1)' : 'var(--muted)',
                        border: `1px solid ${selectedMethod === method.value ? 'rgba(124,58,237,0.4)' : 'var(--border)'}`,
                      }}
                    >
                      <input
                        type="radio"
                        value={method.value}
                        checked={selectedMethod === method.value}
                        onChange={() => setSelectedMethod(method.value)}
                        className="accent-primary"
                      />
                      <method.icon size={16} style={{ color: selectedMethod === method.value ? '#A78BFA' : 'var(--muted-foreground)' }} />
                      <div className="flex-1">
                        <p className="text-sm font-semibold" style={{ color: selectedMethod === method.value ? '#A78BFA' : 'var(--foreground)' }}>
                          {method.label}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{method.sublabel}</p>
                      </div>
                      <span className="text-xs font-bold" style={{ color: '#10B981' }}>Fee: {method.fee}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Account details */}
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-foreground" htmlFor="withdraw-account">
                  {selectedMethod === 'bank' ? 'Bank Account Number / IBAN' : 'UPI ID'}
                </label>
                <input
                  id="withdraw-account"
                  type="text"
                  placeholder={selectedMethod === 'bank' ? 'e.g. HDFC0001234 — 9876543210' : 'e.g. arjun@upi'}
                  className="w-full px-4 py-2.5 text-sm rounded-lg outline-none transition-all"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.accountDetails ? '#F43F5E' : 'var(--border)'}`,
                    color: 'var(--foreground)',
                  }}
                  onFocus={(e) => { if (!errors.accountDetails) e.target.style.borderColor = 'var(--primary)'; }}
                  onBlur={(e) => { if (!errors.accountDetails) e.target.style.borderColor = 'var(--border)'; }}
                  {...register('accountDetails', { required: 'Please enter your account details' })}
                />
                {errors.accountDetails && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.accountDetails.message}</p>}
              </div>

              {/* Compliance notice */}
              <div className="flex items-start gap-2 p-3 rounded-xl"
                style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <AlertTriangle size={13} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '1px' }} />
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  All withdrawals undergo a compliance review (typically 24–72 hours). You may be asked to provide additional identity documentation. Withdrawals may be held if unusual activity is detected on your account.
                </p>
              </div>

              <button type="submit" className="btn-primary w-full justify-center py-3 text-base">
                <ArrowUpRight size={16} />
                Review Withdrawal
              </button>
            </form>
          )}

          {step === 'review' && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--muted)' }}>
                <p className="text-sm font-bold text-foreground mb-3">Withdrawal Summary</p>
                {[
                  { label: 'Amount', value: `${symbol}${numAmount.toFixed(2)}`, highlight: true },
                  { label: 'Method', value: withdrawMethods.find((m) => m.value === selectedMethod)?.label || '' },
                  { label: 'Processing fee', value: 'Free' },
                  { label: 'Review time', value: '24–72 hours' },
                  { label: 'Remaining balance', value: `${symbol}${(availableBalance - numAmount).toFixed(2)}` },
                ].map((item) => (
                  <div key={`wr-${item.label}`} className="flex justify-between text-xs">
                    <span style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
                    <span className="font-semibold" style={{ color: item.highlight ? '#A78BFA' : 'var(--foreground)', fontVariantNumeric: 'tabular-nums' }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-2 p-3 rounded-xl"
                style={{ background: 'rgba(6,182,212,0.06)', border: '1px solid rgba(6,182,212,0.2)' }}>
                <Clock size={13} style={{ color: '#06B6D4', flexShrink: 0, marginTop: '1px' }} />
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  Your withdrawal will enter the compliance review queue. You&apos;ll receive an email notification when it&apos;s processed. Funds will be locked during review and cannot be used for competitions.
                </p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep('form')} className="btn-ghost flex-1 justify-center py-2.5">
                  Back
                </button>
                <button onClick={onConfirm} className="btn-primary flex-1 justify-center py-2.5">
                  <Shield size={14} />
                  Submit Withdrawal
                </button>
              </div>
            </div>
          )}

          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 size={36} className="animate-spin" style={{ color: '#A78BFA' }} />
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">Submitting withdrawal...</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Running compliance checks</p>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(6,182,212,0.2)', border: '2px solid #06B6D4' }}>
                <Check size={28} style={{ color: '#06B6D4' }} />
              </div>
              <div>
                <p className="text-xl font-extrabold text-foreground mb-1">Withdrawal Submitted</p>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {symbol}{numAmount.toFixed(2)} is under compliance review. Expected processing: 24–72 hours.
                </p>
              </div>
              <div className="w-full space-y-2 p-4 rounded-xl" style={{ background: 'var(--muted)' }}>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--muted-foreground)' }}>Request ID</span>
                  <span className="font-mono font-semibold text-foreground">BB-WDR-{Math.floor(Math.random() * 9000 + 1000)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--muted-foreground)' }}>Status</span>
                  <span className="font-semibold tx-pending">Pending Review</span>
                </div>
              </div>
              <button onClick={onClose} className="btn-primary w-full justify-center py-2.5">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}